import { useMemo, useState, useEffect } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  Badge,
  SkeletonBodyText,
  IndexFilters,
  ChoiceList,
  Page,
  IndexFiltersMode,
  Icon,
  Box,
  InlineStack,
  IndexFiltersProps,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { AppPageWrapper } from "@shared/ui";
import { TabsTableSkeleton } from "@shared/ui";
import { IndexTableHeading } from "@medentic/polaris-react/build/ts/src/components/IndexTable/IndexTable";
import { NonEmptyArray } from "@medentic/polaris-react/build/ts/src/types";
import type { Brand, Event } from "@features/events/types";
import { ShieldCheckMarkIcon } from "@medentic/polaris-icons";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";
import { useCountries } from "@shared/hooks/common/useCountries";
import { MultiSelect } from "@shared/ui";
import { BrandFilterOptionsBuilder } from "./BrandFilterOptionsBuilder";

export default function EventBrands() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const serializedSearchParams = searchParams.toString();
  const { GET } = useApiRequest();
  const { countries } = useCountries();

  // Fetch all events
  const { data: allEvents = [] } = useQuery<Event[]>({
    queryKey: ["all-events", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/events`),
    enabled: Boolean(hcc),
  });

  // Only mine events
  const myEvents = useMemo(
    () => allEvents.filter((e: Event) => e.isMy),
    [allEvents]
  );

  // Tabs for roles (example: All, My brands)
  const tabs = [
    { content: t("brands.all"), index: 0, id: "all" },
    { content: t("brands.myBrands"), index: 1, id: "myBrands" },
  ];

  // Initialize state from URL params
  const [selectedTab, setSelectedTab] = useState(() => {
    const status = searchParams.get("status");
    if (status === "myBrands") return 1;
    return 0;
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    () => searchParams.get("roles")?.split(",").filter(Boolean) || []
  );
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    () => searchParams.get("countries")?.split(",").filter(Boolean) || []
  );
  const [selectedEventId, setSelectedEventId] = useState(
    () => searchParams.get("eventIds") || ""
  );
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(
    () => searchParams.get("search") || ""
  );
  const [sortSelected, setSortSelected] = useState<string[]>(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) return [sortParam];
    
    // Check localStorage for saved sorting preference
    const savedSort = localStorage.getItem("events_brands_sorting");
    return savedSort ? [savedSort] : ["name asc"];
  });
  const [mode, setMode] = useState(IndexFiltersMode.Default);

  // Sort options
  const sortOptions: IndexFiltersProps['sortOptions'] = [
    {label: t('brands.sort_by.created'), value: 'created asc', directionLabel: t('common.oldestToNewest')},
    {label: t('brands.sort_by.created'), value: 'created desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('brands.sort_by.modified'), value: 'updated asc', directionLabel: t('common.oldestToNewest')},
    {label: t('brands.sort_by.modified'), value: 'updated desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('brands.sort_by.name'), value: 'name asc', directionLabel: t('common.AtoZ')},
    {label: t('brands.sort_by.name'), value: 'name desc', directionLabel: t('common.ZtoA')},
  ];

  // Save sorting preference to localStorage
  useEffect(() => {
    if (sortSelected[0]) {
      localStorage.setItem("events_brands_sorting", sortSelected[0]);
    }
  }, [sortSelected]);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTab === 1) params.set("status", "myBrands");
    if (selectedRoles.length > 0) params.set("roles", selectedRoles.join(","));
    if (selectedCountries.length > 0)
      params.set("countries", selectedCountries.join(","));
    if (selectedEventId) params.set("eventIds", selectedEventId);
    if (debouncedQuery) params.set("search", debouncedQuery);
    if (sortSelected[0]) params.set("sort", sortSelected[0]);
    setSearchParams(params);
  }, [
    selectedTab,
    selectedRoles,
    selectedCountries,
    selectedEventId,
    debouncedQuery,
    sortSelected,
    setSearchParams,
  ]);

  // Fetch brands
  const {
    data: brands = [],
    isLoading,
    isFetching,
  } = useQuery<Brand[]>({
    queryKey: [
      "event-brands",
      hcc,
      selectedTab,
      selectedRoles,
      selectedCountries,
      selectedEventId,
      debouncedQuery,
      sortSelected,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedTab === 1) params.append("usedBefore", "true");
      if (selectedRoles.length > 0) {
        params.append("roles", selectedRoles.join(","));
      }
      if (selectedCountries.length > 0) {
        params.append("countries", selectedCountries.join(","));
      }
      if (selectedEventId) params.append("eventIds", selectedEventId);
      if (debouncedQuery) params.append("query", debouncedQuery);
      if (sortSelected[0]) params.append("sort", sortSelected[0]);
      return GET(`/api/healthcare-companies/${hcc}/e/brands?${params}`);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  // Table headings
  const headings = [
    { title: t("brands.name") || "Name" },
    { title: t("brands.role") || "Role" },
    { title: t("brands.city") || "City" },
    { title: t("brands.country") || "Country" },
  ];

  // Table rows
  const rowMarkup = brands.map((b, idx) => (
    <IndexTable.Row 
      id={b.id} 
      key={b.id} 
      position={idx}
      onClick={() =>
        navigate(
          `/e/${hcc}/brands/${b.id}${
            serializedSearchParams ? `?${serializedSearchParams}` : ""
          }`
        )
      }
    >
      <IndexTable.Cell>
        <InlineStack gap="200" blockAlign="center">
          <EventsDefaultAvatar name={b.name} singleImage={b.logoSquare} />
          <span>
            {getLocalizedText(b.name)}
          </span>
          {b.verified && (
            <Box>
              <Icon source={ShieldCheckMarkIcon} tone="success" />
            </Box>
          )}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge
          tone={
            b.role === "organizer" || b.role === "Organizer"
              ? "success"
              : undefined
          }
        >
          {b.role ? b.role.charAt(0).toUpperCase() + b.role.slice(1) : "-"}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{getLocalizedText(b.geoAddress?.city) || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{getLocalizedText(b.geoAddress?.country) || "-"}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  // Prepare choices for filters based on actual brand data
  const filterOptionsBuilder = new BrandFilterOptionsBuilder(
    brands,
    countries,
    {
      organizer: t("brands.organizer"),
      sponsor: t("brands.sponsor"),
    }
  );

  const { roles: roleChoices, countries: countryChoices } = 
    filterOptionsBuilder.getAllOptions();
  const filters = [
    {
      key: "role",
      label: t("brands.role"),
      filter: (
        <MultiSelect
          label={t("brands.role")}
          placeholder={t("brands.role")}
          options={roleChoices}
          selected={selectedRoles}
          onChange={setSelectedRoles}
        />
      ),
      pinned: true,
    },
    {
      key: "country",
      label: t("brands.country"),
      filter: (
        <MultiSelect
          label={t("brands.country")}
          placeholder={t("brands.country")}
          options={countryChoices}
          selected={selectedCountries}
          onChange={setSelectedCountries}
        />
      ),
      pinned: true,
    },
    {
      key: "event",
      label: t("brands.event"),
      filter: (
        <ChoiceList
          title={t("brands.event")}
          titleHidden
          choices={myEvents.map((ev) => ({
            label: ev.publicTitle,
            value: ev.id,
          }))}
          selected={selectedEventId ? [selectedEventId] : []}
          onChange={(val) => setSelectedEventId(val[0] || "")}
          allowMultiple={false}
        />
      ),
      pinned: true,
    },
  ];

  // Applied filters
  const appliedFilters = [];
  
  selectedRoles.forEach((roleValue) => {
    const roleLabel =
      roleChoices.find(
        (r: { label: string; value: string }) => r.value === roleValue
      )?.label || roleValue;
    appliedFilters.push({
      key: "role",
      label: `${t("brands.role")}: ${roleLabel}`,
      onRemove: () =>
        setSelectedRoles((prev) => prev.filter((r) => r !== roleValue)),
    });
  });
  
  selectedCountries.forEach((countryValue) => {
    const countryLabel =
      countryChoices.find(
        (c: { label: string; value: string }) => c.value === countryValue
      )?.label || countryValue;
    appliedFilters.push({
      key: "country",
      label: `${t("brands.country")}: ${countryLabel}`,
      onRemove: () =>
        setSelectedCountries((prev) => prev.filter((c) => c !== countryValue)),
    });
  });
  
  if (selectedEventId) {
    appliedFilters.push({
      key: "event",
      label: `${t("brands.event")}: ${
        myEvents.find((ev) => ev.id === selectedEventId)?.publicTitle || ""
      }`,
      onRemove: () => setSelectedEventId(""),
    });
  }

  const isInitialLoading = isLoading && brands.length === 0;

  if (isInitialLoading) {
    return (
      <AppPageWrapper>
        <Page fullWidth title={t("brands.title")}>
          <TabsTableSkeleton />
        </Page>
      </AppPageWrapper>
    );
  }

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("brands.title")}
        primaryAction={{
          content: t("brands.addBrand"),
          onAction: () => navigate(`/e/${hcc}/brands/new`),
        }}
      >
        <Card padding="0">
          <BlockStack gap="0">
            <IndexFilters
              tabs={tabs}
              selected={selectedTab}
              onSelect={setSelectedTab}
              queryValue={queryValue}
              queryPlaceholder={t("brands.searchPlaceholder")}
              onQueryChange={setQueryValue}
              onQueryClear={() => setQueryValue("")}
              filters={filters}
              cancelAction={{
                onAction: () => {
                  setSelectedTab(0);
                  setSelectedRoles([]);
                  setSelectedCountries([]);
                  setSelectedEventId("");
                  setQueryValue("");
                  setSortSelected(["name asc"]);
                },
                disabled: false,
                loading: false,
              }}
              appliedFilters={appliedFilters}
              onClearAll={() => {
                setSelectedTab(0);
                setSelectedRoles([]);
                setSelectedCountries([]);
                setSelectedEventId("");
                setQueryValue("");
                setSortSelected(["name asc"]);
              }}
              sortOptions={sortOptions}
              sortSelected={sortSelected}
              onSort={setSortSelected}
              mode={mode}
              setMode={setMode}
              loading={isFetching && brands.length > 0}
            />
            <IndexTable
              resourceName={{
                singular: t("brands.brand"),
                plural: t("brands.brands"),
              }}
              itemCount={brands.length}
              selectable={false}
              headings={headings as NonEmptyArray<IndexTableHeading>}
            >
              {isLoading
                ? [...Array(5)].map((_, i) => (
                    <IndexTable.Row id={`skeleton-${i}`} key={i} position={i}>
                      {headings.map((_, j) => (
                        <IndexTable.Cell key={j}>
                          <SkeletonBodyText lines={1} />
                        </IndexTable.Cell>
                      ))}
                    </IndexTable.Row>
                  ))
                : rowMarkup}
            </IndexTable>
          </BlockStack>
        </Card>
      </Page>
    </AppPageWrapper>
  );
}
